// Телефоны человека и проба уведомлений: запросы и состояние пробы для блока «Телефон» во
// вкладке «Доступ».
//
// ## Зачем отдельный файл, а не строки в `queries.ts`
//
// `queries.ts` раздела уже больше пятисот строк, и про телефоны в нём говорить нечего: это не
// карточка сотрудника, а учётная запись человека (`/users/{id}/devices`). Корень ключей тот же,
// что у списка учётных записей (`ключиУчётных.все`): выдали ссылку, закрыли вход — и телефоны
// перечитываются вместе со всем остальным, отдельной команды на сброс не нужно.
//
// ## Проба живёт в кэше мутаций, а не в состоянии блока
//
// Вкладки карточки держит Radix, и неактивная вкладка из разметки УХОДИТ: человек нажал
// «Проверить уведомления», прочитал «нет ключа, сообщите студии», заглянул во вкладку «Обзор»
// за телефоном сотрудника и вернулся — а ответа нет. Повторное нажатие раньше десяти секунд
// сервер отклонит, то есть ответ оказался бы потерян и перечитать его нечем. Состояние в самом
// блоке эту поездку не переживает, кэш мутаций — переживает: он живёт в клиенте запросов, выше
// вкладок и выше карточки.
//
// Сколько: до пяти минут после того, как блок ушёл с экрана (срок жизни мутации без
// наблюдателей по умолчанию). Живёт он только в памяти: на диск мутации не сохраняются, а при
// выходе из системы кэш очищается вместе с запросами (`app/providers.tsx`).

import { useState } from 'react';

import {
  useMutation,
  useMutationState,
  useQuery,
  type MutationState,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { DeviceOut, DeviceTestOut } from '@/shared/api/endpoints/devices';
import { listUserDevices, testUserDevices } from '@/shared/api/endpoints/devices';
import type { ApiError } from '@/shared/errors/api-error';
import type { ErrorEnvelope } from '@/shared/api/types';
import { ключиУчётных } from '@/shared/users/queries';

export const ключиТелефонов = {
  список: (userId: string) => [...ключиУчётных.все, 'телефоны', userId] as const,
  проба: (userId: string) => [...ключиУчётных.все, 'проба-уведомлений', userId] as const,
};

/** Сколько сервер не пускает повторную пробу на того же человека. Отсчёт — от отправки. */
export const ПАУЗА_ПРОБЫ_МС = 10_000;

/**
 * Те же слова, что у серверного ответа 429 (`services/devices.py`, `_не_чаще`).
 *
 * Свои, а не выдуманные: нажатие раньше срока на экране и то же нажатие, дошедшее до сервера
 * (например, из второго окна), обязаны выглядеть одинаково. Иначе человек читает две разные
 * просьбы подождать и решает, что у них разные причины.
 */
const ПОДОЖДИТЕ: ErrorEnvelope = {
  code: 'too_many_requests',
  message: 'проверка только что была',
  hint: 'пробное уведомление уже отправлено, оно может ещё идти',
  next_action: 'подождите десять секунд и повторите',
};

/**
 * Телефоны человека, отцеплённые тоже.
 *
 * Свежесть нулевая намеренно, как у списка учётных записей рядом: про доступ и уведомления
 * «немного устаревшие данные» — не неточность, а прямая ложь. Сотрудница переустановила
 * приложение и вошла, а администратор по тридцатисекундной давности читает «адрес отцеплён» —
 * и просит её переустановить ещё раз. Показывается при этом последнее известное немедленно
 * (правило 11), свежее подменяет его.
 */
export function useУстройстваЧеловека(
  userId: string,
  включено: boolean,
): UseQueryResult<DeviceOut[], ApiError> {
  return useQuery<DeviceOut[], ApiError>({
    queryKey: ключиТелефонов.список(userId),
    queryFn: ({ signal }) => listUserDevices(userId, signal),
    staleTime: 0,
    enabled: включено,
  });
}

/** Удачная проба и когда она ушла. */
export interface УдачнаяПроба {
  данные: DeviceTestOut;
  /**
   * Момент, когда запрос ушёл, в миллисекундах. Не момент ответа: свои десять секунд сервер
   * отсчитывает от начала обработки, и считать от ответа значило бы пустить человека на
   * повтор, который сервер тут же отклонит.
   */
  ушлаВ: number;
}

export interface СостояниеПробы {
  /** Запрос ушёл, ответа ещё нет. */
  идёт: boolean;
  /**
   * Последняя удачная проба этого человека. Держится, пока человек не нажал снова и не пришла
   * новая: пока идёт новая, показывается прежняя (блок не схлопывается и не прыгает), а её
   * замена — одним движением, без пустоты между.
   */
  итог: УдачнаяПроба | null;
  /** Отказ последней попытки, кроме «рано»: сети нет, права нет, человека не видно. */
  отказ: ApiError | null;
  /** Проверять рано: запрос даже не ушёл либо сервер ответил 429. Слова те же в обоих случаях. */
  подождать: ErrorEnvelope | null;
  запустить: () => void;
}

/**
 * Проба уведомлений на телефонах человека: запуск и всё, что нужно показать о её ходе.
 *
 * Подтверждения НЕТ намеренно: итог стоит прямо под кнопкой, на том месте, куда человек
 * смотрит, и всплывающее «Проверено» рядом повторяло бы его вдвое короче и другими словами.
 */
export function useПробаУведомлений(userId: string): СостояниеПробы {
  const ключ = ключиТелефонов.проба(userId);
  const [нажалиРано, setНажалиРано] = useState(false);

  const запрос = useMutation<DeviceTestOut, ApiError, void>({
    mutationKey: ключ,
    mutationFn: () => testUserDevices(userId),
  });

  // Все пробы этого человека в порядке отправки: последняя решает, что на экране, а последняя
  // УДАЧНАЯ — что показывать, когда новая ещё не пришла или её отклонили как слишком раннюю.
  const попытки = useMutationState({
    filters: { mutationKey: ключ },
    select: (попытка) => попытка.state as MutationState<DeviceTestOut, ApiError, void>,
  });
  const последняя = попытки.at(-1);
  const удачная = попытки.findLast((попытка) => попытка.status === 'success');

  const отклонёнСервером = последняя?.status === 'error' && последняя.error?.status === 429;
  const идёт = последняя?.status === 'pending';

  // Прежнюю пробу держим на экране, пока идёт новая и пока новую отклонили как раннюю. Любая
  // другая неудача (сети нет, права нет) заменяет её собой: человек нажал, чтобы узнать
  // свежее, и показывать ему старый ответ рядом с ошибкой значило бы выдавать его за свежий.
  const держатьПрежнюю = последняя?.status === 'success' || идёт || отклонёнСервером === true;

  const запустить = () => {
    if (идёт) {
      // Кнопка в этот момент и так «Отправляем…» и недоступна; сюда попадает только
      // двойное нажатие клавишей, и второй запрос здесь стоил бы первого.
      return;
    }
    if (удачная !== undefined && Date.now() - удачная.submittedAt < ПАУЗА_ПРОБЫ_МС) {
      setНажалиРано(true);
      return;
    }
    setНажалиРано(false);
    запрос.mutate();
  };

  return {
    идёт,
    итог:
      держатьПрежнюю && удачная !== undefined
        ? { данные: удачная.data as DeviceTestOut, ушлаВ: удачная.submittedAt }
        : null,
    отказ: последняя?.status === 'error' && !отклонёнСервером ? последняя.error : null,
    подождать:
      отклонёнСервером && последняя?.error != null
        ? последняя.error.envelope
        : нажалиРано
          ? ПОДОЖДИТЕ
          : null,
    запустить,
  };
}
